import { create } from 'zustand'
import api from '../api/client'
import type { User } from '../types'

// Helper to check if JWT token is expired
function isTokenExpired(token: string): boolean {
  try {
    const payload = token.split('.')[1]
    if (!payload) return true

    const decoded = JSON.parse(atob(payload))
    if (!decoded.exp) return true // No expiry claim - treat as invalid/expired

    // Check if token expires within next 5 minutes (buffer for clock skew)
    const expiryTime = decoded.exp * 1000
    const bufferMs = 5 * 60 * 1000
    return Date.now() > expiryTime - bufferMs
  } catch {
    return true // If we can't decode, treat as expired
  }
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  initialize: () => Promise<void>
  sendCode: (email: string) => Promise<void>
  verifyCode: (email: string, code: string) => Promise<{ isNewUser: boolean }>
  logout: () => Promise<void>
  updateUser: (user: Partial<User>) => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  initialize: async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      set({ isLoading: false })
      return
    }

    // Check if token is expired before making API call
    if (isTokenExpired(token)) {
      localStorage.removeItem('token')
      set({ isLoading: false })
      return
    }

    try {
      const user = await api.get<User>('/auth/me')
      set({ user, isAuthenticated: true, isLoading: false })
    } catch {
      localStorage.removeItem('token')
      set({ isLoading: false })
    }
  },

  sendCode: async (email: string) => {
    set({ error: null })
    try {
      await api.post('/auth/send-code', { email })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send code'
      set({ error: message })
      throw err
    }
  },

  verifyCode: async (email: string, code: string) => {
    set({ error: null })
    try {
      const response = await api.post<{ token: string; user: User; isNewUser: boolean }>('/auth/verify-code', {
        email,
        code,
      })
      localStorage.setItem('token', response.token)
      set({ user: response.user, isAuthenticated: true })
      return { isNewUser: response.isNewUser }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification failed'
      set({ error: message })
      throw err
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      // Ignore errors - we'll clear local state anyway
    }
    localStorage.removeItem('token')
    set({ user: null, isAuthenticated: false })
  },

  updateUser: (updates: Partial<User>) => {
    const { user } = get()
    if (user) {
      set({ user: { ...user, ...updates } })
    }
  },

  clearError: () => set({ error: null }),
}))
